/**
 * MUI Video Component
 * 
 * This component is the Material UI equivalent of the original Video component.
 * It renders a YouTube video player for the Room Designer application.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import YouTube from 'react-youtube';
import ReducersIndex from 'reduxModules/ducks';
import isEmpty from 'lodash/isEmpty';
import { AnyAction } from '@reduxjs/toolkit';
import type { RootState } from 'types/redux/state';

/**
 * Interface for YouTube player properties
 */
interface YouTubePlayer {
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  stopVideo: () => void;
  getDuration: () => number;
  getCurrentTime: () => number;
  playerInfo?: {
    playerState: number;
  };
}

/**
 * Interface for YouTube player event
 */
interface YouTubeEvent {
  target: YouTubePlayer;
}

/**
 * Interface for YouTube options
 */
interface YouTubeOpts {
  width: string;
  height: string;
  frameborder: string;
  playerVars: {
    autoplay: number;
    modestbranding: number;
    showinfo: number;
    [key: string]: any;
  };
}


/**
 * MUI Video component
 * 
 * @param props - Component props
 * @returns The MUI Video component
 */
const MuiVideo: React.FC = (props) => {
  const [playing, setPlaying] = useState<boolean>(false);
  // Using any type for the YouTube ref since it's a third-party component
  const playerRef = useRef<any>(null);
  
  const { modalOpen: tutorialOpen } = useSelector((state: RootState) => 
    state.ui.modals.find(modal => modal.modalName === 'tutorial') || { modalOpen: false }
  );

  useEffect(() => {
    // Only proceed if the ref has been assigned
    if (!playerRef.current) return;
    
    const player = playerRef.current.getInternalPlayer() as YouTubePlayer | undefined;
    if (tutorialOpen) {
      if (!isEmpty(player)) player?.seekTo(0, true);
    } else {
      if (!isEmpty(player)) player?.stopVideo();
    }
  }, [tutorialOpen]);

  const dispatch = useDispatch();
  
  useEffect(() => {
    if (playing) {
      const interval = setInterval(async () => {
        if (!playerRef.current) return;
        
        const player = playerRef.current.getInternalPlayer() as YouTubePlayer;
        const totalTime = await player.getDuration();
        // This is a promise, don't forget to await
        const elapsedTime = await player.getCurrentTime();
        
        if (elapsedTime >= totalTime * 0.2) {
          // Cast to AnyAction since we don't have exact action type
          dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false) as unknown as AnyAction);
        }
      }, 100); // 100 ms refresh
      
      return () => {
        clearInterval(interval);
      };
    }
  }, [playing, dispatch]);

  /**
   * Handle player state change
   * 
   * @param evt - The state change event
   */
  const handleStateChange = (evt: YouTubeEvent): void => {
    setPlaying(evt.target.playerInfo?.playerState === 1);
  };

  const opts: YouTubeOpts = {
    width: '640',
    height: '360',
    frameborder: '0',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      modestbranding: 1,
      showinfo: 0,
    },
  };

  return (
    <Box 
      className="d-flex justify-content-center embed-responsive embed-responsive-16by9"
    >
      <YouTube 
        ref={playerRef} 
        videoId="8xbovbi715E" 
        opts={opts} 
        onStateChange={(e) => handleStateChange(e)} 
      />
    </Box>
  );
};

export default MuiVideo;
